import React, { useEffect, useRef, useState } from 'react';
import { useA11y } from '../hooks/useA11y.js';
import { useEscapeKey } from '../hooks/useEscapeKey.js';
import { useFocusTrap } from '../hooks/useFocusTrap.js';
import { useDraggable } from '../hooks/useDraggable.js';
import { KEY_MAP, matchesKey } from '../utils/keyMap.js';
import { A11yToolbar } from './A11yToolbar.jsx';
import { CloseIcon } from './icons.jsx';

const KEY_STEP = 16;

export function A11yFloatingPanel({ onOpenHelp } = {}) {
  const a11y = useA11y();
  const [open, setOpen] = useState(false);
  const [draftPos, setDraftPos] = useState(null);
  const panelRef = useRef(null);
  const launcherRef = useRef(null);
  const position = (a11y.settings && a11y.settings.panelPosition) || { right: 24, bottom: 24 };
  const displayPos = draftPos || position;

  const draggable = useDraggable({
    position,
    onChange: (pos) => setDraftPos(pos),
    onCommit: () => {
      if (draftPos) {
        a11y.movePanel(draftPos);
      }
      setDraftPos(null);
    },
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onKey = (e) => {
      if (matchesKey(e, KEY_MAP.TOGGLE_PANEL)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEscapeKey(() => setOpen(false), open);

  useFocusTrap(panelRef, open);

  const onLauncherClick = () => {
    if (draggable.didMove()) return;
    setOpen((o) => !o);
  };
  const onLauncherKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen((o) => !o);
      return;
    }
    if (typeof window === 'undefined') return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const cur = displayPos;
    let next = null;
    if (e.key === 'ArrowUp')    next = { right: cur.right, bottom: Math.min(h - 56, cur.bottom + KEY_STEP) };
    else if (e.key === 'ArrowDown')  next = { right: cur.right, bottom: Math.max(0,      cur.bottom - KEY_STEP) };
    else if (e.key === 'ArrowLeft')  next = { right: Math.min(w - 56, cur.right + KEY_STEP), bottom: cur.bottom };
    else if (e.key === 'ArrowRight') next = { right: Math.max(0,      cur.right - KEY_STEP), bottom: cur.bottom };
    if (next) {
      e.preventDefault();
      setDraftPos(next);
      a11y.movePanel(next);
    }
  };

  return (
    <>
      <div
        className="a11y-floating-panel"
        style={{ right: displayPos.right + 'px', bottom: displayPos.bottom + 'px' }}
      >
        <button
          ref={launcherRef}
          type="button"
          className="a11y-floating-panel__launcher"
          onClick={onLauncherClick}
          onKeyDown={onLauncherKey}
          onPointerDown={draggable.onPointerDown}
          aria-label={open ? 'Close accessibility menu' : 'Open accessibility menu'}
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-controls="a11y-side-panel"
          title="Accessibility menu (Alt+S) — drag to move"
        >
          <span aria-hidden="true">♿︎</span>
        </button>
      </div>
      {open && (
        <>
          <div
            className="a11y-side-panel__backdrop"
            onMouseDown={() => setOpen(false)}
            aria-hidden="true"
          />
          <aside
            ref={panelRef}
            id="a11y-side-panel"
            className="a11y-side-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="a11y-side-panel-title"
          >
            <header className="a11y-side-panel__header">
              <h2 id="a11y-side-panel-title" className="a11y-side-panel__title">
                Accessibility Menu
              </h2>
              <button
                type="button"
                className="a11y-side-panel__close"
                onClick={() => setOpen(false)}
                aria-label="Close accessibility menu"
              >
                <CloseIcon />
              </button>
            </header>
            <div className="a11y-side-panel__body">
              <A11yToolbar onClose={() => setOpen(false)} onOpenHelp={onOpenHelp} />
            </div>
          </aside>
        </>
      )}
    </>
  );
}

export default A11yFloatingPanel;
